'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ClipboardList, Loader2, LogOut, Package, Plus, RefreshCw, Search, Trash2,
  Check, X, ImageOff, MessageSquarePlus,
} from 'lucide-react'

import { useAuth } from '@/hooks/useAuth'
import { useCompany } from '@/hooks/useCompany'
import { FunctionsService } from '@/services/functions.service'
import { NotesService } from '@/services/notes.service'
import { cn } from '@/lib/utils'
import type { CatalogProduct, ProductVariant, SalesNote } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'

// Normaliza para comparar sin distinguir mayúsculas ni acentos
const normalize = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()

interface NoteDraft {
  productId: string | null
  productName: string | null
  variant: string | null
  requestedItem: string
}

export function AyudaVentasDashboard() {
  const { user, role, signOut } = useAuth()
  const company = useCompany()
  const navigate = useNavigate()

  const [products, setProducts] = useState<CatalogProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchMode, setSearchMode] = useState<'variant' | 'name'>('variant')

  // Notas
  const [notesOpen, setNotesOpen] = useState(false)
  const [notes, setNotes] = useState<SalesNote[]>([])
  const [notesLoading, setNotesLoading] = useState(false)

  // Diálogo de nueva nota
  const [draft, setDraft] = useState<NoteDraft | null>(null)
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)

  const loadProducts = useCallback(async () => {
    setLoading(true)
    setError(null)
    const res = await FunctionsService.getProductsFromFirestore()
    if (res.error) {
      setError(res.error)
      setProducts([])
    } else {
      const list: CatalogProduct[] = (res.data?.products ?? []).map((p: any) => ({
        id: String(p.id),
        title: p.title ?? '',
        inventory: Number(p.inventory) || 0,
        imageUrl: p.imageUrl ?? null,
        handle: p.handle ?? null,
        status: p.status ?? 'active',
        collections: Array.isArray(p.collections) ? p.collections : [],
        variants: Array.isArray(p.variants)
          ? p.variants.map((v: any) => ({
              title: v.title ?? 'Talla única',
              inventory: Number(v.inventory) || 0,
              sku: v.sku ?? '',
              price: Number(v.price) || 0,
            }))
          : [],
      }))
      setProducts(list)
    }
    setLoading(false)
  }, [])

  const loadNotes = useCallback(async () => {
    setNotesLoading(true)
    try {
      setNotes(await NotesService.getNotes(100))
    } catch {
      /* noop */
    }
    setNotesLoading(false)
  }, [])

  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  const term = normalize(search)

  // En modo talla basta 1 carácter; en modo nombre pedimos 2 para no inundar
  const minLen = searchMode === 'variant' ? 1 : 2

  const results = useMemo(() => {
    if (term.length < minLen) return []
    return products
      .map((p) => {
        // Solo tallas/variantes con stock disponible
        const availableVariants = (p.variants ?? []).filter((v) => v.inventory > 0)
        const availableUnits = availableVariants.reduce((sum, v) => sum + v.inventory, 0)
        return { ...p, variants: availableVariants, inventory: availableUnits || p.inventory }
      })
      .filter((p) => {
        const hasVariantInfo = (p.variants ?? []).length > 0
        // Mostrar solo productos con algo disponible en tienda
        const inStock = hasVariantInfo ? p.variants!.length > 0 : p.inventory > 0
        if (!inStock) return false
        if (searchMode === 'variant') {
          // Solo por talla/variante disponible (el nombre del producto no interfiere)
          return (p.variants ?? []).some((v) => normalize(v.title).includes(term))
        }
        // Modo nombre: solo por título del producto
        return normalize(p.title).includes(term)
      })
      .sort((a, b) => b.inventory - a.inventory)
      .slice(0, 60)
  }, [products, term, searchMode, minLen])

  const openNote = (product?: CatalogProduct, variant?: ProductVariant) => {
    setComment('')
    setDraft({
      productId: product?.id ?? null,
      productName: product?.title ?? null,
      variant: variant?.title ?? null,
      requestedItem: product?.title ?? search.trim(),
    })
  }

  const handleSaveNote = async () => {
    if (!draft) return
    const requestedItem = draft.requestedItem.trim()
    if (!requestedItem && !comment.trim()) return
    setSaving(true)
    try {
      await NotesService.addNote({
        requestedItem,
        note: comment,
        productId: draft.productId,
        productName: draft.productName,
        variant: draft.variant,
        sellerEmail: user?.email ?? null,
        sellerName: user?.displayName ?? user?.email ?? null,
      })
      setDraft(null)
      setComment('')
      if (notesOpen) loadNotes()
    } catch {
      /* noop */
    }
    setSaving(false)
  }

  const toggleNotes = () => {
    const next = !notesOpen
    setNotesOpen(next)
    if (next) loadNotes()
  }

  const handleSignOut = async () => {
    try { await signOut() } catch { /* noop */ }
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      <div className="pointer-events-none fixed inset-0 flex items-center justify-center opacity-[0.015]">
        <img src={company.watermarkUrl || '/nibpr.svg'} alt="" className="h-96 w-auto select-none" />
      </div>

      {/* Barra superior */}
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-white/10 bg-gray-950/90 px-4 py-3 backdrop-blur-xl">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={company.logoUrl ?? '/LogoNibpr.png'}
            alt={company.shortName}
            className="h-8 w-8 shrink-0 rounded object-contain"
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-bold leading-tight">Ayudaventas</p>
            <p className="truncate text-xs text-white/40">
              {user?.displayName || user?.email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {role === 'admin' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="hidden gap-1.5 border-white/10 bg-white/10 text-white hover:bg-white/20 sm:flex"
            >
              Panel admin
            </Button>
          )}
          <button
            onClick={handleSignOut}
            className="rounded-lg p-2 text-white/40 transition-colors hover:bg-red-500/10 hover:text-red-400"
            aria-label="Cerrar sesión"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="relative mx-auto w-full max-w-[1100px] px-4 py-6 pb-28 sm:px-6">
        <div className="mb-4">
          <h1 className="text-xl font-bold">¿Qué talla buscas?</h1>
          <p className="text-sm text-white/50">
            Busca el producto para ver la foto y las tallas disponibles en tienda.
          </p>
        </div>

        {/* Selector de modo de búsqueda */}
        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs text-white/40">Buscar por</span>
          <div className="inline-flex rounded-md border border-white/15 bg-white/5 p-0.5">
            {([
              { key: 'variant', label: 'Talla' },
              { key: 'name', label: 'Nombre' },
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setSearchMode(key)}
                className={cn(
                  'rounded px-3 py-1.5 text-xs font-medium transition',
                  searchMode === key
                    ? 'bg-blue-600 text-white'
                    : 'text-white/60 hover:text-white'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Buscador + acciones */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                searchMode === 'variant'
                  ? 'Buscar por talla… (ej. 10W, 42, M)'
                  : 'Buscar por producto… (ej. Nike, camiseta)'
              }
              className="border-white/15 bg-white/10 pl-9 pr-9 text-white placeholder:text-white/40 focus-visible:ring-blue-500"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition hover:text-white"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => openNote()}
            className="gap-1.5 border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
          >
            <MessageSquarePlus className="h-4 w-4" />
            No disponible
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={toggleNotes}
            className={cn(
              'gap-1.5 border-white/10 bg-white/10 text-white hover:bg-white/20',
              notesOpen && 'border-blue-500 bg-blue-500 hover:bg-blue-600'
            )}
          >
            <ClipboardList className="h-4 w-4" />
            Notas
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={loadProducts}
            disabled={loading}
            title="Recargar catálogo"
            className="gap-1.5 border-white/10 bg-white/10 text-white hover:bg-white/20"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {/* Panel de notas */}
        {notesOpen && (
          <NotesPanel
            notes={notes}
            loading={notesLoading}
            onRefresh={loadNotes}
            onDelete={async (id) => { await NotesService.deleteNote(id); loadNotes() }}
            onResolve={async (n) => {
              await NotesService.setStatus(n.id!, n.status === 'resuelto' ? 'abierto' : 'resuelto')
              loadNotes()
            }}
          />
        )}

        {/* Resultados */}
        <div className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-40 w-full bg-white/10" />
              ))}
            </div>
          ) : term.length < minLen ? (
            <div className="rounded-lg border border-dashed border-white/15 p-10 text-center text-white/50">
              {searchMode === 'variant'
                ? 'Escribe una talla para ver los productos disponibles.'
                : 'Escribe al menos 2 letras del nombre del producto.'}
            </div>
          ) : results.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/15 p-10 text-center text-white/60">
              <p>No hay ningún producto que coincida con “{search.trim()}”.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openNote()}
                className="mt-4 gap-1.5 border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
              >
                <Plus className="h-4 w-4" />
                Guardar como pedido del cliente
              </Button>
            </div>
          ) : (
            <>
              <p className="mb-3 text-xs text-white/40">
                {results.length} resultado{results.length === 1 ? '' : 's'}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {results.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    highlight={searchMode === 'variant' ? term : ''}
                    onNote={(v) => openNote(p, v)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Diálogo de nueva nota */}
      <Dialog open={draft !== null} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="border-white/10 bg-gray-900 text-white">
          <DialogHeader>
            <DialogTitle>Nota de venta</DialogTitle>
            <DialogDescription className="text-white/50">
              Registra lo que busca el cliente para tenerlo en cuenta (tallas, colores,
              productos que no hay en tienda).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs text-white/60">Producto / lo que busca</label>
              <Input
                value={draft?.requestedItem ?? ''}
                onChange={(e) => setDraft((d) => (d ? { ...d, requestedItem: e.target.value } : d))}
                placeholder="Ej. Tenis running talla 42 negro"
                className="border-white/15 bg-white/10 text-white placeholder:text-white/40"
              />
            </div>
            {draft?.variant && (
              <p className="text-xs text-white/50">
                Talla solicitada: <span className="text-white">{draft.variant}</span>
              </p>
            )}
            <div>
              <label className="mb-1 block text-xs text-white/60">Comentario</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Notas adicionales (nombre del cliente, contacto, etc.)"
                className="w-full rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDraft(null)}
              className="border-white/15 bg-white/5 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveNote}
              disabled={saving || (!draft?.requestedItem.trim() && !comment.trim())}
              className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Guardar nota
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Tarjeta de producto ──────────────────────────────────────────────────
function ProductCard({
  product,
  onNote,
  highlight = '',
}: {
  product: CatalogProduct
  onNote: (variant?: ProductVariant) => void
  highlight?: string
}) {
  const [imgError, setImgError] = useState(false)
  const variants = product.variants ?? []
  const hasStock = product.inventory > 0

  return (
    <Card className="overflow-hidden border-white/10 bg-white/5 text-white">
      <CardContent className="flex flex-col p-3">
        {/* Nombre del producto */}
        <p className="line-clamp-2 text-sm font-semibold leading-tight">{product.title}</p>

        {/* Cantidad disponible */}
        <p
          className={cn(
            'mt-1 inline-flex items-center gap-1 text-xs',
            hasStock ? 'text-emerald-400' : 'text-red-400'
          )}
        >
          <Package className="h-3.5 w-3.5" />
          {hasStock ? `${product.inventory} en tienda` : 'Sin stock'}
        </p>

        {/* Foto grande (fondo blanco: unifica fotos con transparencia y horizontales) */}
        <div className="mt-3 aspect-square w-full overflow-hidden rounded-lg bg-white">
          {product.imageUrl && !imgError ? (
            <img
              src={product.imageUrl}
              alt={product.title}
              loading="lazy"
              onError={() => setImgError(true)}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray-300">
              <ImageOff className="h-12 w-12" />
            </div>
          )}
        </div>

        {/* Tallas / variantes con stock */}
        {variants.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {variants.map((v, i) => {
              const available = v.inventory > 0
              const isMatch = highlight
                ? v.title.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(highlight)
                : false
              return (
                <button
                  key={`${v.title}-${i}`}
                  type="button"
                  onClick={() => onNote(v)}
                  title={available ? `${v.inventory} disponibles · dejar nota` : 'Agotada · dejar nota'}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition',
                    available
                      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20'
                      : 'border-white/10 bg-white/5 text-white/35 line-through hover:bg-white/10',
                    isMatch && 'ring-2 ring-blue-400/70'
                  )}
                >
                  <span className="font-medium">{v.title}</span>
                  <span className={available ? 'text-emerald-300/80' : 'text-white/30'}>
                    {available ? `×${v.inventory}` : '0'}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNote()}
            className="w-full gap-1.5 border-white/10 bg-white/5 text-xs text-white/70 hover:bg-white/10 hover:text-white"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
            Dejar nota
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Panel de notas ───────────────────────────────────────────────────────
function NotesPanel({
  notes,
  loading,
  onRefresh,
  onDelete,
  onResolve,
}: {
  notes: SalesNote[]
  loading: boolean
  onRefresh: () => void
  onDelete: (id: string) => void
  onResolve: (note: SalesNote) => void
}) {
  return (
    <Card className="mt-4 border-white/10 bg-white/5 text-white">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <ClipboardList className="h-4 w-4 text-blue-400" />
            Notas de venta ({notes.length})
          </h2>
          <button
            onClick={onRefresh}
            className="rounded-md p-1.5 text-white/40 hover:bg-white/10 hover:text-white"
            aria-label="Actualizar notas"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
        </div>

        {loading ? (
          <Skeleton className="h-24 w-full bg-white/10" />
        ) : notes.length === 0 ? (
          <p className="py-6 text-center text-sm text-white/40">
            Aún no hay notas registradas.
          </p>
        ) : (
          <ul className="space-y-2">
            {notes.map((n) => (
              <li
                key={n.id}
                className={cn(
                  'rounded-lg border p-3 text-sm',
                  n.status === 'resuelto'
                    ? 'border-white/5 bg-white/[0.02] text-white/40'
                    : 'border-white/10 bg-white/5'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={cn('font-medium', n.status === 'resuelto' && 'line-through')}>
                      {n.requestedItem || '(sin título)'}
                      {n.variant && <span className="text-white/50"> · talla {n.variant}</span>}
                    </p>
                    {n.note && <p className="mt-0.5 text-xs text-white/60">{n.note}</p>}
                    <p className="mt-1 text-[10px] text-white/30">
                      {n.sellerName || 'Vendedor'} ·{' '}
                      {new Date(n.createdAt).toLocaleString('es-ES', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      onClick={() => onResolve(n)}
                      title={n.status === 'resuelto' ? 'Marcar como abierto' : 'Marcar como resuelto'}
                      className={cn(
                        'rounded-md p-1.5 transition',
                        n.status === 'resuelto'
                          ? 'text-white/30 hover:bg-white/10 hover:text-white'
                          : 'text-emerald-400/70 hover:bg-emerald-500/10 hover:text-emerald-400'
                      )}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => n.id && onDelete(n.id)}
                      title="Eliminar nota"
                      className="rounded-md p-1.5 text-red-400/60 transition hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

export default AyudaVentasDashboard
